import type { components } from '@sportik/api-coach/schema';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api-client';
import { invitationsKeyPrefix } from '@/services/invitations';
import { rosterKeyPrefix } from '@/services/roster';

type RequestRef = Pick<components['schemas']['Request'], 'id' | 'version'>;

/**
 * The one definition of this read: athlete requests waiting for an answer.
 * Polled, so a new request shows within half a minute (ADR 0016 § 3).
 */
export function requestsOptions() {
  return api.queryOptions(
    'get',
    '/requests',
    { params: { query: { state: ['pending'] } } },
    { refetchInterval: 30_000 },
  );
}

/** The athletes asking to be coached. */
export function useRequests() {
  return useQuery(requestsOptions());
}

function useRequestAnswer(
  path: '/requests/{requestId}/accept' | '/requests/{requestId}/decline',
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, version }: RequestRef) =>
      api.client.POST(path, {
        params: { path: { requestId: id } },
        body: { version },
      }),
    // An answered request leaves the list; an accepted one is a client on the
    // roster, and spends the single-use link it came through.
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: requestsOptions().queryKey.slice(0, 2),
        }),
        queryClient.invalidateQueries({ queryKey: rosterKeyPrefix() }),
        queryClient.invalidateQueries({ queryKey: invitationsKeyPrefix() }),
      ]),
  });
}

/** Accepts a request: the athlete becomes an active client. */
export function useAcceptRequest() {
  return useRequestAnswer('/requests/{requestId}/accept');
}

/** Turns a request down. */
export function useDeclineRequest() {
  return useRequestAnswer('/requests/{requestId}/decline');
}
